import React from "react";
import styled from "@emotion/styled";
import { easelFontFamily } from "../../helpers/constants";

/*
 * TODO: This is a duplicated Design System component and should be removed
 * once we are able to import components from the Design System directly.
 */

export type BootstrapTextVariant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "display1"
  | "display2"
  | "display3"
  | "display4";

export type CustomTextVariant = "body" | "small" | "xsmall";

export type TextVariant = BootstrapTextVariant | CustomTextVariant;

export interface ITextProps {
  className?: string;

  /** Set the text alignment. */
  align?: "inherit" | "left" | "center" | "right";

  /** The content of the component. */
  children: React.ReactNode;

  /** If true, will set the text to all-caps. */
  caps?: boolean;

  /** Set the color based on Bootstrap text color variants. */
  color?: "body" | "inherit";

  /** If true, will set the `display` property to `inline`. */
  inline?: boolean;

  /** If true, will italicize the text. */
  italic?: boolean;

  /** If true, will remove the bottom margin. */
  noMarginBottom?: boolean;

  /** If true, will bold the text. */
  strong?: boolean;

  /** If true, will truncate text on one line with an ellipsis. */
  truncate?: boolean;

  /** Sets the typographic variant. */
  variant?: TextVariant;
}

export type EventKey = string | number;

const colorMap = {
  inherit: "inherit",
  body: "#212529",
};

const variantTagMap = {
  display1: "h1",
  display2: "h1",
  display3: "h1",
  display4: "h1",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  body: "p",
  small: "span",
  xsmall: "span",
};

const variantSizeMap = {
  h1: "2.5rem",
  h2: "2rem",
  h3: "1.75rem",
  h4: "1.5rem",
  h5: "1.25rem",
  body: "1rem",
};

const truncateStyle = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/*
 * Typographic utility component used to render standard body text and headings.
 */
const Text: React.FunctionComponent<ITextProps> = (props) => {
  const {
    align = "inherit",
    caps = false,
    children,
    className,
    color = "body",
    inline = false,
    italic = false,
    noMarginBottom,
    strong = false,
    truncate = false,
    variant = "body",
    ...other
  } = props;

  const componentTag = variantTagMap[variant] || "div";
  const Component = React.useMemo(
    () =>
      styled[componentTag]({
        fontFamily: easelFontFamily,
        fontSize: variantSizeMap[variant] || "1rem",
        lineHeight: "1.5rem",
        marginTop: 0,
        marginBottom: 0,
        fontWeight: strong ? 600 : 400,
        textTransform: caps ? "uppercase" : "none",
        fontStyle: italic ? "italic" : "none",
        textAlign: align,
        display: inline ? "inline" : "default",
        color: colorMap[color],
        ...(truncate ? truncateStyle : {}),
      }),
    [props]
  );

  return <Component {...other}>{children}</Component>;
};

Text.displayName = "Text";

export default Text;
